import { useState, useEffect } from 'react'
import { BrowserRouter } from 'react-router-dom'
import AdminLogin from './AdminLogin'
import AdminShops from './AdminShops'
import App from '../App'

const TOKEN_KEY = 'admin_token'

export default function AdminApp() {
  const [token,      setToken]      = useState(() => localStorage.getItem(TOKEN_KEY) || '')
  const [verified,   setVerified]   = useState(false)

  useEffect(() => {
    if (!token) { setVerified(true); return }
    fetch('/api/admin/stats', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken('') }
        setVerified(true)
      })
      .catch(() => setVerified(true))
  }, [])
  const [viewShop,   setViewShop]   = useState(null)
  const [detailShop, setDetailShop] = useState(null)

  function handleLogin(tok) {
    localStorage.setItem(TOKEN_KEY, tok)
    setToken(tok)
  }

  function handleLogout() {
    localStorage.removeItem(TOKEN_KEY)
    setToken('')
    setViewShop(null)
    setDetailShop(null)
  }

  function handleImpersonate(shop) {
    // A new tab, so the admin keeps their list.
    //
    // This used to rewrite the current URL and render the seller app in place,
    // which meant losing the shop list and whatever had been scrolled or
    // filtered to get to it — and going back to compare two sellers meant
    // starting again. Opening a tab per seller is how anybody actually works
    // through a list of them.
    //
    // The token is NOT carried in sessionStorage any more, because it cannot
    // be: a tab opened with noopener is unrelated to its opener and
    // sessionStorage is not copied into it. The portal reads the admin's own
    // token from localStorage instead, which AdminApp already keeps there.
    // See utils/adminSession.
    //
    // noopener as well as _blank: the new tab has no reason to reach back
    // through window.opener, and a tab that can is a tab that can navigate
    // this one.
    // /dashboard, NOT /.
    //
    // The OAuth router is mounted at the site root — server.js has
    // `app.use('/', shopifyOAuth)` and the install route is
    // `router.get(['/', '/install'])` — so the root path belongs to install.
    // A real GET /?shop=… starts OAuth and the tab lands on Shopify's
    // "Unauthorized Access" page with the portal never shown.
    //
    // It did not happen before because the same-tab version called
    // history.replaceState, which changes the URL without asking the server
    // for it. Opening a tab makes a real request, and the request hits the
    // install route.
    //
    // Any other SPA path falls through to the catchall, which serves the app
    // and strips the shopify-api-key meta for admin_impersonate=1 so App
    // Bridge cannot redirect the tab either. Verified: /dashboard answers 200
    // with no redirects. The Shopify install flow is deliberately untouched.
    const params = new URLSearchParams()
    params.set('shop', shop.shopify_domain)
    params.set('admin_impersonate', '1')
    window.open(`/dashboard?${params.toString()}`, '_blank', 'noopener,noreferrer')
  }

  if (!verified) return null
  if (!token) return <AdminLogin onLogin={handleLogin} />

  // Render seller app directly inside admin — no iframe, no new tab, no Shopify redirect
  if (viewShop) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        {/* Admin banner */}
        <div style={{
          height: 46, background: '#1a0a00', borderBottom: '2px solid #0064d2',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 20px', flexShrink: 0, zIndex: 9999,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: '#0064d2', color: '#fff', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 4, letterSpacing: '.08em' }}>ADMIN VIEW</span>
            <span style={{ color: '#f5a623', fontSize: 13, fontWeight: 600 }}>Viewing: {viewShop.shopify_domain}</span>
            {viewShop.ebay_shop_name && <span style={{ color: '#6b7280', fontSize: 12 }}>· eBay: {viewShop.ebay_shop_name}</span>}
          </div>
          <button onClick={() => {
            sessionStorage.removeItem('admin_impersonate_token')
            window.history.replaceState({}, '', '/admin-panel/')
            setViewShop(null)
          }} style={{
            background: 'rgba(0,100,210,0.15)', border: '1px solid rgba(0,100,210,0.4)',
            color: '#0064d2', borderRadius: 8, padding: '6px 16px',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>← Back to Admin</button>
        </div>
        {/* Render seller app directly — App Bridge never initialised here */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </div>
      </div>
    )
  }

  return (
    <AdminShops
      token={token}
      onLogout={handleLogout}
      onImpersonate={handleImpersonate}
      detailShop={detailShop}
      onShowDetail={setDetailShop}
      onCloseDetail={() => setDetailShop(null)}
    />
  )
}
